import { DOM } from "Library/DOM";
import "./ApertureWindowElement.less";
import { gsap } from "gsap";
import { LocationStore } from "Feature/Map/LocationStore";

export interface Configuration { 
    title: string;
}

export class ApertureWindowElement extends HTMLElement { 
    private _config: Configuration = { title: "Aperture" };
    static tag = "aperture-window";
    private _header: HTMLElement;
    private dispatchWindowChange = () => this.dispatchEvent(new CustomEvent("window-change"));

    constructor(configAction?: (config: Configuration) => void) { 
        super();

        if (configAction)
            configAction(this._config);
        
        this.addEventListener("pointerdown", this.onPointerDown);
    }

    connectedCallback() { 
        if (this.hasAttribute("title"))
            this._config.title = this.getAttribute("title");
        
        
        const width = this.hasAttribute("width") ? parseInt(this.getAttribute("width")) : 480;
        const height = this.hasAttribute("height") ? parseInt(this.getAttribute("height")) : 320;

        this.setSize(width, height);

        if (this.hasAttribute("center"))
            requestAnimationFrame(() => DOM.center(this, document.body));

        const template = this.querySelector("template");
        const headertTemplate = this.querySelector("template[name=header]");
        
        this.innerHTML = this.view();

        
        this.querySelector("section").innerHTML = (<Element>template.cloneNode(true)).innerHTML;

        
        
        this._header = this.querySelector("header") as HTMLElement;

        
        if (headertTemplate)
            this._header.querySelector(".user-area").innerHTML = (<Element>headertTemplate.cloneNode(true)).innerHTML;

    }

    disconnectedCallback() { 
        document.removeEventListener("pointermove", this.onPointerMove);
        document.removeEventListener("pointerup", this.onPointerUp);
        document.addEventListener("pointerleave", this.onPointerUp);

        this.dispatchEvent(new CustomEvent("closed"));
    }

    offscreen() { 
        this.style.transform = "translatey(-200%)";
        return this;
    } 

    async animateOut() { 
        const target = {
            y: 0
        }

        await gsap.timeline()
            .to(target,
                {
                    y: -200,
                    onUpdate: () => { 
                        this.style.transform = `translatey(${target.y}%)`;
                        this.dispatchEvent(new CustomEvent("window-change"));
                    }
                });
    }

    async animateIn() { 
        const target = {
            y: -200
        }

        await gsap.timeline()
            .to(target,
                {
                    y: 0,
                    onUpdate: () => { 
                        this.style.transform = `translatey(${target.y}%)`;
                        this.dispatchEvent(new CustomEvent("window-change"));
                    }
                });
    }

    private _position = { x: 0, y: 0 };

    private _initialSize = { width: 0, height: 0 };

    private onClose = () => {
        this.remove();
    }

    private onPointerDown = (event: PointerEvent) => { 
        if (!(event.target instanceof Element))
            return;
        
        const closeButton = event.target.closest("[name=close]");
        if (closeButton) {
            this.onClose();
            return;
        }
        
        const draggable = event.target.closest("[name=draggable]");
        if (draggable) { 
            this._position = { x: event.clientX, y: event.clientY };

            const style = window.getComputedStyle(this);
            this._initialSize = {
                width: parseInt(style.getPropertyValue("--aperture-width")),
                height: parseInt(style.getPropertyValue("--aperture-height"))
            };

            document.addEventListener("pointermove", this.onPointerResizeMove, { passive: true });
            document.addEventListener("pointerup", this.onPointerResizeUp, { passive: true });
            document.addEventListener("pointerleave", this.onPointerResizeUp, { passive: true });
            draggable.setPointerCapture(event.pointerId);
            return;
        }
        
        const header = event.target.closest("header");
        if (header && header == this._header) { 
            this._position = { x: event.clientX, y: event.clientY };

            document.addEventListener("pointermove", this.onPointerMove, { passive: true });
            document.addEventListener("pointerup", this.onPointerUp, { passive: true });
            document.addEventListener("pointerleave", this.onPointerUp, { passive: true });
            header.setPointerCapture(event.pointerId);
            header.toggleAttribute("moving", true);
        }
    }

    private onPointerResizeMove = (event: PointerEvent) => { 
        const deltaX = event.clientX - this._position.x;
        const deltaY = event.clientY - this._position.y;

        const width = this._initialSize.width + deltaX;
        const height = this._initialSize.height + deltaY;

        this.setSize(width, height);
        this.dispatchWindowChange();
    }

    setSize(width: number, height: number) { 

        width = Math.ceil(width);
        height = Math.ceil(height);

        this.style.setProperty("--aperture-width", `${width}px`);
        this.style.setProperty("--aperture-height", `${height}px`);
        this.style.setProperty("--aperture-inner-width", `${width-20}px`);
        this.style.setProperty("--aperture-inner-height", `${height-48}px`);
    }

    private onPointerResizeUp = (event: PointerEvent) => { 
        document.removeEventListener("pointermove", this.onPointerResizeMove);
        document.removeEventListener("pointerup", this.onPointerResizeUp);
        document.addEventListener("pointerleave", this.onPointerResizeUp);

        this.dispatchWindowChange();
    }

    private onPointerMove = (event: PointerEvent) => { 
        const deltaX = event.clientX - this._position.x;
        const deltaY = event.clientY - this._position.y;

        this.dispatchWindowChange();

        this.style.transform = `translate(${deltaX}px, ${deltaY}px)`;
    }

    private onPointerUp = (event: PointerEvent) => { 
        document.removeEventListener("pointermove", this.onPointerMove);
        document.removeEventListener("pointerup", this.onPointerUp);
        document.addEventListener("pointerleave", this.onPointerUp);
        this._header.toggleAttribute("moving", false);

        const current = this.getBoundingClientRect();
        this.style.left = `${current.left}px`;
        this.style.top = `${Math.max(0, current.top)}px`;
        this.style.transform = ""; 

        this.dispatchWindowChange();
    }

    private view = () => `
        <header>
            <div>
                <div class=icon>
                    <div style="background-color:${LocationStore.routeToColor(this._config.title)}"></div>
                </div>
                <div>${this._config.title}</div>
            </div>
            <div class=user-area></div>
            <button name=close>${require("./Images/close.svg")}</button>
        </header>
        <section>
            
           
        </section>
        <button name=draggable>${require("./Images/draggable.svg")}</button>
    `;
}

customElements.define(ApertureWindowElement.tag, ApertureWindowElement);

// export class TestContent extends HTMLElement { 
//     static tag = "test-content";

//     constructor() { 
//         super();
//         console.log("TestContent constructor");
//     }

//     connectedCallback() { 
//         console.log("TestContent connected");
//         this.innerHTML = this.view();
//     }

//     disconnectedCallback() {
//         console.log("TestContent disconnected");
//     }

//     private view = () => `
//         <h1>Test Content</h1>
//         <p>Content</p>
//     `;
// }
// customElements.define(TestContent.tag, TestContent);